import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MonitorPlay, X, ZoomIn } from "lucide-react";
import { GALLERY } from "../content";
import Reveal from "./Reveal";

const Placeholder = ({ compact = false }) => (
  <div
    className={`flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#EAE6F0] bg-[#FAF8FC] text-center ${
      compact ? "p-2" : "p-8"
    }`}
  >
    <MonitorPlay size={compact ? 22 : 40} className="text-[#FF008B]" aria-hidden="true" />
    {!compact && (
      <p className="text-sm font-semibold text-[#68677A]">Tu dodamy podgląd platformy</p>
    )}
  </div>
);

const Slide = ({ item, compact = false, eager = false, cover = false }) =>
  item.src ? (
    <img
      src={item.src}
      alt={item.alt}
      loading={eager ? "eager" : "lazy"}
      className={
        cover
          ? "h-full w-full rounded-lg object-cover object-top"
          : "mx-auto block max-h-[70vh] w-auto max-w-full"
      }
    />
  ) : (
    <Placeholder compact={compact} />
  );

export default function Gallery() {
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const lastFocusRef = useRef(null);
  const closeRef = useRef(null);

  const openLightbox = useCallback(() => {
    lastFocusRef.current = document.activeElement;
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    if (lastFocusRef.current) lastFocusRef.current.focus();
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxOpen, closeLightbox]);

  const item = GALLERY[current];

  return (
    <section id="podglad" className="bg-[#FAF8FC] py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <h2 className="text-2xl font-bold tracking-tight text-[#242344] sm:text-3xl lg:text-4xl">
            Zobacz, jak wygląda kurs od środka
          </h2>
          <p className="mt-3 max-w-2xl text-base text-[#34343F] sm:text-lg">
            Zajrzyj na platformę i zobacz, jak wyglądają lekcje oraz program kursu
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8">
            <button
              type="button"
              data-testid="gallery-main-view"
              onClick={openLightbox}
              aria-label={`Powiększ podgląd: ${item.caption}`}
              className="group relative block w-full cursor-zoom-in"
            >
              <div className="browser-frame mx-auto w-fit max-w-full">
                <div className="flex items-center gap-2 border-b border-[#EAE6F0] bg-[#FAF8FC] px-4 py-2.5">
                  <span className="flex gap-1.5" aria-hidden="true">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FF008B]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FFB3DA]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#242344]/25" />
                  </span>
                  <span className="mx-auto hidden rounded-full bg-white px-4 py-1 text-xs font-semibold text-[#68677A] shadow-sm sm:block">
                    online.edunation.com.pl
                  </span>
                </div>
                <div className="bg-white p-2 sm:p-3">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      <Slide item={item} eager />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
              <span className="absolute bottom-5 right-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#242344] text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true">
                <ZoomIn size={18} />
              </span>
            </button>
            <p className="mt-3 text-sm font-medium text-[#68677A]">{item.caption}</p>

            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3" role="group" aria-label="Wybór podglądu">
              {GALLERY.map((g, i) => (
                <button
                  key={g.id}
                  type="button"
                  data-testid={`gallery-thumbnail-${g.id}`}
                  onClick={() => setCurrent(i)}
                  aria-pressed={i === current}
                  aria-label={`Pokaż podgląd: ${g.caption}`}
                  className={`rounded-xl border-2 p-1.5 transition-all duration-200 sm:p-2 ${
                    i === current
                      ? "border-[#FF008B] shadow-md shadow-[#FF008B]/10"
                      : "border-transparent hover:border-[#FFB3DA]"
                  }`}
                >
                  <div className="aspect-[4/3]">
                    <Slide item={g} compact cover />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            data-testid="gallery-lightbox-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Powiększony podgląd: ${item.caption}`}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#242344]/80 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <motion.div
              className="relative w-full max-w-5xl rounded-2xl bg-white p-3 shadow-2xl sm:p-5"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex max-h-[75vh] items-center justify-center">
                <Slide item={item} eager />
              </div>
              <p className="mt-3 text-center text-sm font-medium text-[#68677A]">{item.caption}</p>
              <button
                ref={closeRef}
                type="button"
                data-testid="gallery-lightbox-close"
                onClick={closeLightbox}
                aria-label="Zamknij powiększenie"
                className="absolute -right-2 -top-2 flex h-11 w-11 items-center justify-center rounded-full bg-[#FF008B] text-white shadow-lg transition-colors duration-200 hover:bg-[#D90077]"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
